/**
 * LevelCheck — a round, tri-state check mark for rows in a cumulative list.
 *
 * Checkbox semantics (react-aria Checkbox): `checked`, `mixed` (some of the
 * things this row stands for are on) or `unchecked`, announced as a checkbox
 * with `aria-checked` and toggled with Space. Visually it is the round mark
 * the LevelStepper uses for its nodes, so a stepper and the list beneath it
 * read as one control.
 *
 * Pass `children` to make the visible label part of the click target (a leaf
 * row). Omit them and pass `aria-label` when the row's text belongs to
 * something else — an accordion trigger beside the mark, say.
 *
 * `isReadOnly` renders a mark that cannot change (an "always on" row): it is
 * focusable and announced as read-only, and shows no hover affordance.
 */

import type { ReactNode } from 'react';
import { Checkbox as AriaCheckbox } from 'react-aria-components';

import { cx } from '@/utils/cx';

export type LevelCheckState = 'checked' | 'mixed' | 'unchecked';

export type LevelCheckSize = 'md' | 'sm';

export interface LevelCheckProps {
  state: LevelCheckState;
  /**
   * A state being previewed (something elsewhere is hovered and this mark
   * would change). Drawn as a GHOST, never as the real state: a mark that
   * would turn on takes a light brand fill with a brand glyph; one that
   * would turn off fades. Flipping to the real state and back on every
   * hover reads as a flash. Assistive tech is told the real state only.
   */
  preview?: LevelCheckState | null;
  /** Fires with the state the press asks for: `true` from unchecked or
   *  mixed, `false` from checked. */
  onChange?(next: boolean): void;
  /** Fires while the pointer is over the mark, so a consumer can preview
   *  what a press would do. Never fires while disabled or read-only. */
  onHoverChange?(isHovering: boolean): void;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  size?: LevelCheckSize;
  /** Required when there are no children. */
  'aria-label'?: string;
  /** The visible label, inside the click target. */
  children?: ReactNode;
  className?: string;
}

const MOTION = 'duration-200 ease-out motion-reduce:transition-none';

const SIZES: Record<
  LevelCheckSize,
  { holder: string; mark: string; check: string; bar: string }
> = {
  // The holder is the hit target (24px); the mark inside is 16px, so a
  // `-mx-1` keeps the visible column the width of the mark.
  md: {
    holder: '-mx-1 size-6',
    mark: 'size-4',
    check: 'size-2.5',
    bar: 'h-0.5 w-2',
  },
  sm: {
    holder: '-mx-1 size-6',
    mark: 'size-3.5',
    check: 'size-2',
    bar: 'h-0.5 w-1.5',
  },
};

const SHELL_BY_STATE: Record<LevelCheckState, string> = {
  checked: 'bg-brand-solid',
  mixed: 'bg-primary ring-1 ring-brand ring-inset',
  unchecked: 'bg-primary ring-1 ring-primary ring-inset',
};

/** The ghost shell for a mark that would turn on (or gain a check). */
const GHOST_ON_SHELL = 'bg-brand-secondary ring-1 ring-brand ring-inset';

export function LevelCheck({
  state,
  preview = null,
  onChange,
  onHoverChange,
  isDisabled = false,
  isReadOnly = false,
  size = 'md',
  children,
  className,
  'aria-label': ariaLabel,
}: LevelCheckProps) {
  const sizes = SIZES[size];
  const interactive = !isDisabled && !isReadOnly;
  const ghost = preview !== null && preview !== state;
  // Would turn on (or up): draw the previewed glyph in brand on a light
  // fill. Would turn off: keep the real glyph and fade the whole mark.
  const ghostOn = ghost && preview !== 'unchecked';
  const ghostOff = ghost && preview === 'unchecked';
  const glyphState = ghostOn ? preview : state;
  return (
    <AriaCheckbox
      isSelected={state === 'checked'}
      isIndeterminate={state === 'mixed'}
      onChange={onChange}
      onHoverChange={interactive ? onHoverChange : undefined}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      aria-label={ariaLabel}
      className={cx(
        'group/check flex items-center gap-3 outline-hidden select-none',
        isDisabled && 'cursor-not-allowed',
        isReadOnly && 'cursor-default',
        interactive && 'cursor-pointer',
        className
      )}
    >
      {({ isFocusVisible }) => (
        <>
          <span
            className={cx(
              'flex shrink-0 items-center justify-center',
              sizes.holder
            )}
          >
            {/* One element whose background, ring and inner glyphs transition
                between states — swapping icons would cut, not animate. */}
            <span
              aria-hidden='true'
              data-preview={ghost ? preview : undefined}
              className={cx(
                'relative flex items-center justify-center rounded-full transition-[background-color,box-shadow,opacity]',
                MOTION,
                sizes.mark,
                ghostOn ? GHOST_ON_SHELL : SHELL_BY_STATE[state],
                ghostOff && 'opacity-40',
                interactive &&
                  !ghost &&
                  state === 'unchecked' &&
                  'group-hover/check:ring-brand',
                isFocusVisible &&
                  'outline-2 outline-offset-2 outline-focus-ring'
              )}
            >
              <svg
                viewBox='0 0 12 12'
                className={cx(
                  'transition-[opacity,transform,color]',
                  MOTION,
                  sizes.check,
                  ghostOn ? 'text-fg-brand-primary' : 'text-fg-white',
                  glyphState === 'checked'
                    ? 'scale-100 opacity-100'
                    : 'scale-50 opacity-0'
                )}
                fill='none'
                stroke='currentColor'
                strokeWidth={2.2}
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <path d='M2.5 6.5l2.3 2.3L9.5 3.5' />
              </svg>
              <span
                className={cx(
                  'absolute rounded-full bg-fg-brand-primary transition-[opacity,transform]',
                  MOTION,
                  sizes.bar,
                  glyphState === 'mixed'
                    ? 'scale-100 opacity-100'
                    : 'scale-50 opacity-0'
                )}
              />
            </span>
          </span>
          {children}
        </>
      )}
    </AriaCheckbox>
  );
}
